import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { store } from '../data/store';
import { alertEngine } from '../data/alerts';
import { OrderBook } from '../data/orderbook';
import { C, F } from '../styles/palette';
import { crisp, type CanvasSurface } from '../lib/frame';
import { compact, fixed, price as fmtPrice, qty as fmtQty } from '../lib/num';
import { useCanvasPanel, usePersisted, useStoreTick, useStructure } from './hooks';

const ROW_H = 20;
const MID_H = 34;
const HEADER_H = 15;

type BarMode = 'size' | 'cumulative';

/**
 * Live order book ladder.
 *
 * Drawn to canvas rather than DOM. A 40-level book updating ten times a second
 * is 400 DOM text mutations per second per side; as canvas it is one repaint
 * of a few hundred rectangles, and it stays flat when the book is idle because
 * the frame task checks `book.seq` before doing anything.
 */
export function OrderBookPanel() {
  useStructure();
  useStoreTick();

  // Defaults chosen for legibility, not completeness:
  //  - 'size' scales each bar against the largest single resting order, so the
  //    ladder shows where liquidity actually sits. 'cumulative' is monotonic,
  //    which means the far rows are full-width by definition and the panel
  //    reads as one solid block.
  //  - Raw grouping on a deep book is a wall of one-tick dust levels; a few
  //    ticks per row aggregates them into levels worth looking at.
  const [barMode, setBarMode] = usePersisted<BarMode>('terminal.book.bars', 'size');
  const [groupIndex, setGroupIndex] = usePersisted<number>('terminal.book.group', 2);
  const hoverRef = useRef<{ y: number; price: number } | null>(null);
  const [, force] = useState(0);

  const symbol = store.active;
  const st = store.get(symbol);
  const book = st.book;

  // Grouping steps derived from the exchange tick size, so the options are
  // always real increments for this symbol rather than arbitrary round numbers.
  const groups = useMemo(() => {
    const tick = st.meta?.tickSize ?? Math.pow(10, -st.pricePrecision);
    return [0, tick, tick * 5, tick * 10, tick * 50, tick * 100, tick * 500];
  }, [st.meta, st.pricePrecision]);
  const group = groups[Math.min(groupIndex, groups.length - 1)] ?? 0;

  const lastSeqRef = useRef(-1);
  const lastSymbolRef = useRef('');

  const draw = useCallback(
    (surface: CanvasSurface) => {
      const { ctx, width, height } = surface;
      drawBook(ctx, width, height, {
        book,
        precision: st.pricePrecision,
        group,
        barMode,
        last: st.ticker.last,
        direction: st.ticker.direction,
        hoverY: hoverRef.current?.y ?? -1,
      });
      lastSeqRef.current = book.seq;
      lastSymbolRef.current = symbol;
    },
    [book, st, group, barMode, symbol],
  );

  const canvasRef = useCanvasPanel(draw, {
    priority: 5,
    dirty: () =>
      book.seq !== lastSeqRef.current ||
      symbol !== lastSymbolRef.current ||
      hoverRef.current !== null,
  });

  // Reset the memo of what we drew when the socket forces a resync.
  useEffect(() => store.bookReset.on(() => (lastSeqRef.current = -1)), []);

  const onMove = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      const el = e.currentTarget;
      const r = el.getBoundingClientRect();
      const y = e.clientY - r.top;
      const p = priceAtY(y, r.height, book, group);
      hoverRef.current = { y, price: p };
    },
    [book, group],
  );

  const onLeave = useCallback(() => {
    hoverRef.current = null;
    force((n) => n + 1);
  }, []);

  const onClick = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      const el = e.currentTarget;
      const r = el.getBoundingClientRect();
      const p = priceAtY(e.clientY - r.top, r.height, book, group);
      if (Number.isFinite(p) && p > 0) {
        alertEngine.add(symbol, p);
        force((n) => n + 1);
      }
    },
    [book, group, symbol],
  );

  const levels = book.getLevels(1, group);
  const spreadBps =
    Number.isFinite(levels.spread) && levels.mid > 0 ? (levels.spread / levels.mid) * 10_000 : NaN;
  const imb = book.imbalance(0.002);

  const stateLabel =
    book.state === 'live'
      ? `${book.depthCount} lvl`
      : book.state === 'syncing'
        ? 'syncing…'
        : book.state === 'resyncing'
          ? 'resyncing…'
          : 'no book';

  return (
    <div className="panel">
      <div className="panel-head">
        <span className="panel-title">Order book</span>
        <span className="sub">{symbol}</span>
        <span className="spacer" />
        <span
          className="sub"
          title={book.lastResyncReason ?? 'Book synchronised from REST snapshot + diff stream'}
        >
          {stateLabel}
        </span>
        {book.resyncCount > 0 && (
          <span className="badge" title={`Resynchronised ${book.resyncCount}x`}>
            ↻{book.resyncCount}
          </span>
        )}
        <button
          className="btn sm ghost"
          onClick={() => setBarMode(barMode === 'size' ? 'cumulative' : 'size')}
          title="Depth bars show resting size at each level, or the cumulative total"
        >
          {barMode === 'size' ? 'size' : 'cum'}
        </button>
        <button
          className="btn sm ghost"
          onClick={() => setGroupIndex((i) => (i + 1) % groups.length)}
          title="Group price levels"
        >
          {group === 0 ? 'raw' : fixed(group, decimalsOf(group))}
        </button>
      </div>

      <div className="book-strip" title="Resting size within 0.2% of mid">
        <div className="book-strip-bar">
          <div className="book-strip-bid" style={{ width: `${(imb.ratio * 100).toFixed(1)}%` }} />
        </div>
        <span className="up num">{compact(imb.bid)}</span>
        <span className="muted">{(imb.ratio * 100).toFixed(0)}%</span>
        <span className="down num">{compact(imb.ask)}</span>
      </div>

      <div className="panel-body">
        <canvas
          ref={canvasRef}
          className="panel-canvas"
          onPointerMove={onMove}
          onPointerLeave={onLeave}
          onPointerDown={onClick}
          title="Click a level to set a price alert there"
        />
        {book.state !== 'live' && (
          <div className="placeholder">{book.state === 'empty' ? 'awaiting depth' : 'syncing book'}</div>
        )}
      </div>

      <div className="book-foot">
        <span className="mono-label">Spread</span>
        <span className="num">
          {Number.isFinite(levels.spread) ? fmtPrice(levels.spread, st.pricePrecision) : '—'}
        </span>
        <span className="muted num">{Number.isFinite(spreadBps) ? `${spreadBps.toFixed(1)}bp` : ''}</span>
        <span className="spacer" />
        <span className="mono-label">Mid</span>
        <span className="num">
          {Number.isFinite(levels.mid) ? fmtPrice(levels.mid, st.pricePrecision) : '—'}
        </span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

interface DrawArgs {
  book: OrderBook;
  precision: number;
  group: number;
  barMode: BarMode;
  last: number;
  direction: number;
  hoverY: number;
}

/** Rows available on one side, given the canvas height. */
function rowsPerSide(height: number): number {
  return Math.max(1, Math.floor((height - MID_H - HEADER_H) / 2 / ROW_H));
}

function priceAtY(y: number, height: number, book: OrderBook, group: number): number {
  const rows = rowsPerSide(height);
  const levels = book.getLevels(rows, group);
  const askTop = HEADER_H;
  const askBottom = askTop + rows * ROW_H;

  if (y >= askTop && y < askBottom) {
    // Asks are drawn best-last, so the row index counts back from the bottom.
    const i = rows - 1 - Math.floor((y - askTop) / ROW_H);
    return i >= 0 && i < levels.askCount ? levels.askPrice[i] : NaN;
  }
  const bidTop = askBottom + MID_H;
  if (y >= bidTop) {
    const i = Math.floor((y - bidTop) / ROW_H);
    return i >= 0 && i < levels.bidCount ? levels.bidPrice[i] : NaN;
  }
  return NaN;
}

function drawBook(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  args: DrawArgs,
): void {
  const { book, precision, group, barMode, last, direction, hoverY } = args;

  ctx.fillStyle = C.bg1;
  ctx.fillRect(0, 0, width, height);

  const rows = rowsPerSide(height);
  const levels = book.getLevels(rows, group);

  // Column layout, right-aligned so digits line up under each other.
  const padR = 8;
  const colTotal = width - padR;
  const colSize = colTotal - 62;
  const colPrice = 8;

  // Header
  ctx.font = F.micro;
  ctx.fillStyle = C.text3;
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'left';
  ctx.fillText('PRICE', colPrice, HEADER_H / 2);
  ctx.textAlign = 'right';
  ctx.fillText('SIZE', colSize, HEADER_H / 2);
  ctx.fillText('TOTAL', colTotal, HEADER_H / 2);
  ctx.strokeStyle = C.line;
  ctx.beginPath();
  ctx.moveTo(0, crisp(HEADER_H));
  ctx.lineTo(width, crisp(HEADER_H));
  ctx.stroke();

  const scale = barMode === 'size' ? levels.maxQty : levels.maxCum;
  const askTop = HEADER_H;
  const askBottom = askTop + rows * ROW_H;
  const bidTop = askBottom + MID_H;

  // -- asks: drawn top-down in descending price, so best ask sits just above
  // the spread block. Index 0 is the best ask, hence the reversed row order.
  for (let i = 0; i < rows; i++) {
    const y = askTop + (rows - 1 - i) * ROW_H;
    if (i >= levels.askCount) continue;
    const p = levels.askPrice[i];
    const q = levels.askQty[i];
    const cum = levels.askCum[i];
    drawRow(ctx, {
      y,
      width,
      colPrice,
      colSize,
      colTotal,
      price: p,
      qty: q,
      cum,
      precision,
      fill: 'rgba(246,70,93,0.17)',
      text: C.down,
      value: barMode === 'size' ? q : cum,
      scale,
      flash: fade(book.flashAge(p)),
      flashRgb: '246,70,93',
      zebra: i % 2 === 1,
      hovered: hoverY >= y && hoverY < y + ROW_H,
    });
  }

  // -- mid block: last price, spread
  ctx.fillStyle = C.bg2;
  ctx.fillRect(0, askBottom, width, MID_H);
  ctx.strokeStyle = C.line;
  ctx.beginPath();
  ctx.moveTo(0, crisp(askBottom));
  ctx.lineTo(width, crisp(askBottom));
  ctx.moveTo(0, crisp(askBottom + MID_H));
  ctx.lineTo(width, crisp(askBottom + MID_H));
  ctx.stroke();

  const midY = askBottom + MID_H / 2;
  ctx.textAlign = 'left';
  ctx.font = F.lgBold;
  ctx.fillStyle = direction > 0 ? C.up : direction < 0 ? C.down : C.text0;
  ctx.fillText(Number.isFinite(last) ? fmtPrice(last, precision) : '—', colPrice, midY - 1);

  // Direction arrow next to the price
  if (Number.isFinite(last) && direction !== 0) {
    const tw = ctx.measureText(fmtPrice(last, precision)).width;
    ctx.font = F.sm;
    ctx.fillText(direction > 0 ? '▲' : '▼', colPrice + tw + 6, midY);
  }

  ctx.font = F.micro;
  ctx.textAlign = 'right';
  ctx.fillStyle = C.text2;
  if (Number.isFinite(levels.spread)) {
    ctx.fillText(`spread ${fmtPrice(levels.spread, precision)}`, colTotal, midY - 6);
    const bps = levels.mid > 0 ? (levels.spread / levels.mid) * 10_000 : NaN;
    if (Number.isFinite(bps)) ctx.fillText(`${bps.toFixed(1)} bp`, colTotal, midY + 7);
  }

  // -- bids
  for (let i = 0; i < rows; i++) {
    const y = bidTop + i * ROW_H;
    if (y + ROW_H > height) break;
    if (i >= levels.bidCount) continue;
    const p = levels.bidPrice[i];
    const q = levels.bidQty[i];
    const cum = levels.bidCum[i];
    drawRow(ctx, {
      y,
      width,
      colPrice,
      colSize,
      colTotal,
      price: p,
      qty: q,
      cum,
      precision,
      fill: 'rgba(46,189,133,0.17)',
      text: C.up,
      value: barMode === 'size' ? q : cum,
      scale,
      flash: fade(book.flashAge(p)),
      flashRgb: '46,189,133',
      zebra: i % 2 === 1,
      hovered: hoverY >= y && hoverY < y + ROW_H,
    });
  }
}

/**
 * Flash strength for a level that changed `age` ms ago.
 *
 * A binary "changed recently" test is wrong on a busy book: on BTCUSDT very
 * nearly every visible level changes within any 400ms window, so a constant
 * highlight applies to every row at once and simply raises the panel's
 * background. Decaying it means only the levels that moved *this instant*
 * stand out from the ones that moved a moment ago.
 */
function fade(age: number): number {
  if (!(age < OrderBook.FLASH_MS)) return 0;
  return Math.max(0, 1 - age / OrderBook.FLASH_MS);
}

interface RowArgs {
  y: number;
  width: number;
  colPrice: number;
  colSize: number;
  colTotal: number;
  price: number;
  qty: number;
  cum: number;
  precision: number;
  fill: string;
  text: string;
  value: number;
  scale: number;
  /** 0..1 recency of the last size change at this level. */
  flash: number;
  flashRgb: string;
  zebra: boolean;
  hovered: boolean;
}

function drawRow(ctx: CanvasRenderingContext2D, a: RowArgs): void {
  const h = ROW_H - 1;

  if (a.zebra) {
    ctx.fillStyle = 'rgba(255,255,255,0.012)';
    ctx.fillRect(0, a.y, a.width, h);
  }

  // Depth bar, anchored right so the ladder reads as a horizontal histogram.
  const frac = a.scale > 0 ? Math.min(1, a.value / a.scale) : 0;
  const barW = frac * a.width;
  ctx.fillStyle = a.fill;
  ctx.fillRect(a.width - barW, a.y, barW, h);

  if (a.flash > 0) {
    ctx.fillStyle = `rgba(${a.flashRgb},${(a.flash * 0.13).toFixed(3)})`;
    ctx.fillRect(0, a.y, a.width, h);
  }
  if (a.hovered) {
    ctx.fillStyle = 'rgba(255,255,255,0.055)';
    ctx.fillRect(0, a.y, a.width, h);
  }

  const mid = a.y + ROW_H / 2 - 0.5;
  ctx.font = F.xs;
  ctx.textBaseline = 'middle';

  ctx.textAlign = 'left';
  ctx.fillStyle = a.text;
  ctx.fillText(fmtPrice(a.price, a.precision), a.colPrice, mid);

  ctx.textAlign = 'right';
  ctx.fillStyle = C.text0;
  ctx.fillText(fmtQty(a.qty), a.colSize, mid);

  ctx.fillStyle = C.text3;
  ctx.fillText(compact(a.cum), a.colTotal, mid);
}

function decimalsOf(step: number): number {
  if (step >= 1) return 0;
  const s = step.toExponential();
  const exp = Number(s.slice(s.indexOf('e') + 1));
  return Math.min(8, Math.max(0, -exp));
}
